
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { TJob, TJobPagination, TJobStatus } from "apiTypes/jobs";
import JobCard from "components/Jobs/JobCard";
import CustomJumbotron from "components/ReuseableCompos/CustomJumbotron";
import MolhamButton from "components/ReuseableCompos/MolhamButton";
import SEO from "components/Seo";
import { GetServerSideProps } from "next";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { getJobsWithMeta } from "utils/jobs";
type Props = {
    jobs: TJob[];
    pagination: TJobPagination;
    status: TJobStatus;
};
const STATUSES: TJobStatus[] = ["all", "open", "closed"];
const Jobs = ({ jobs, pagination, status }: Props) => {
    const { t } = useTranslation("common");
    const router = useRouter();
    const [locationFilter, setLocationFilter] = useState("");
    // Location options derived from the visible jobs — no extra API call.
    const locationOptions = useMemo(() => {
        const set = new Set<string>();
        jobs.forEach((job) => {
            if (job.location)
                set.add(job.location);
        });
        return Array.from(set);
    }, [jobs]);
    // Order is controlled by the API — filter only, never re-sort.
    const filteredJobs = locationFilter
        ? jobs.filter((job) => job.location === locationFilter)
        : jobs;
    const changeStatus = (next: TJobStatus) => {
        router.push({ pathname: "/jobs", query: { status: next } });
    };
    const goToPage = (page: number) => {
        router.push({ pathname: "/jobs", query: { status, page } });
    };
    return (<>
      <SEO title={t("jobs")} description={t("jobs_desc")}/>
      <JobsContainer>
        <CustomJumbotron title={t("jobs")} desc={t("jobs_page_description")} p="py-4 px-0" m="mt-0 mb-4" breadCrumbM="my-0" className="mt-0 border-bottom"/>
        <div className="container py-5">
          {/* Filters */}
          <FilterSection className="mb-4">
            <StatusToggle>
              {STATUSES.map((s) => (<FilterButton key={s} active={status === s} onClick={() => changeStatus(s)}>
                  {t(s === "all" ? "all" : s === "open" ? "active" : "closed")}
                </FilterButton>))}
            </StatusToggle>

            {locationOptions.length > 0 && (<LocationSelect value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} aria-label={t("location")}>
                <option value="">{t("all_locations")}</option>
                {locationOptions.map((loc) => (<option key={loc} value={loc}>
                    {loc}
                  </option>))}
              </LocationSelect>)}
          </FilterSection>

          {/* Grid */}
          {filteredJobs.length > 0 ? (<JobsGrid>
              {filteredJobs.map((job) => (<JobCard key={job.id} data={job}/>))}
            </JobsGrid>) : (<NoDataSection>
              <NoDataIcon className="fe-inbox"/>
              <NoDataText>{t("no_jobs_found")}</NoDataText>
              <NoDataDescription>{t("no_jobs_description")}</NoDataDescription>
            </NoDataSection>)}

          {/* Pagination */}
          {pagination.last_page > 1 && (<PaginationSection className="mt-5">
              <MolhamButton id="previous-jobs-page" type="button" onClick={() => goToPage(pagination.current_page - 1)} disabled={pagination.current_page === 1} label={t("previous")} startIcon="fe-chevron-left"/>
              <PaginationInfo>
                {t("page")} {pagination.current_page} {t("of")}{" "}
                {pagination.last_page}
              </PaginationInfo>
              <MolhamButton id="next-jobs-page" type="button" onClick={() => goToPage(pagination.current_page + 1)} disabled={pagination.next_page_url === null} label={t("next")} endIcon="fe-chevron-right"/>
            </PaginationSection>)}
        </div>
      </JobsContainer>
    </>);
};
export default Jobs;
const getServerSideProps: GetServerSideProps = async (context) => {
    const status = (context.query.status as TJobStatus) || "all";
    const page = parseInt(context.query.page as string) || undefined;
    try {
        const { jobs, pagination } = await getJobsWithMeta({ status, page });
        return { props: { jobs, pagination, status } };
    }
    catch {
        return {
            props: {
                jobs: [],
                pagination: {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0,
                    from: null,
                    to: null,
                    next_page_url: null,
                    prev_page_url: null
                },
                status
            }
        };
    }
};
// Styled Components
const JobsContainer = styled.div `
  min-height: 100vh;
  background-color: var(--bs-body-bg);
`;
const FilterSection = styled.div `
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding: 1rem;
`;
const StatusToggle = styled.div `
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const FilterButton = styled.button<{
    active: boolean;
}> `
  padding: 0.5rem 1rem;
  border: 1px solid
    ${({ active }) => (active ? "transparent" : "var(--bs-primary)")};
  background-color: ${({ active }) => active ? "var(--bs-primary)" : "transparent"};
  color: ${({ active }) => (active ? "#fff" : "var(--bs-body-color)")};
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ active }) => active ? "var(--bs-primary)" : "var(--bs-primary-bg-subtle)"};
    border-color: var(--bs-primary);
  }
`;
const LocationSelect = styled.select `
  padding: 0.5rem 2.25rem 0.5rem 1rem;
  border: 1px solid var(--bs-primary);
  border-radius: 0.5rem;
  background-color: transparent;
  color: var(--bs-body-color);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.3s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236c757d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  [dir="rtl"] & {
    padding: 0.5rem 1rem 0.5rem 2.25rem;
    background-position: left 0.75rem center;
  }

  &:hover {
    background-color: var(--bs-primary-bg-subtle);
    border-color: var(--bs-primary);
  }

  &:focus {
    outline: none;
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 0.2rem var(--bs-primary-bg-subtle);
  }
`;
const JobsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;
const NoDataSection = styled.div `
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
`;
const NoDataIcon = styled.i `
  font-size: 4rem;
  color: var(--bs-secondary-color);
  opacity: 0.5;
  margin-bottom: 1.5rem;
`;
const NoDataText = styled.h3 `
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--bs-heading-color);
  margin-bottom: 0.5rem;
`;
const NoDataDescription = styled.p `
  font-size: 1rem;
  color: var(--bs-secondary-color);
`;
const PaginationSection = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;
const PaginationInfo = styled.div `
  font-size: 1rem;
  font-weight: 600;
  color: var(--bs-heading-color);
  padding: 0 1rem;

  @media (max-width: 768px) {
    order: -1;
  }
`;

    async function __Next_Translate__getServerSideProps__1a13f66c038__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/jobs/index',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a13f66c038__ as getServerSideProps }
  